import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { DomSanitizer } from "@angular/platform-browser";
import { switchMap } from "rxjs/operators";
import { ListingDetail } from "./listing-detail";
import { TorontoListingsService } from "./toronto-listings.service";
import { SEOService } from "./seo.service";
import { ScrollToModule } from "@nicky-lenaers/ngx-scroll-to";

import { Globals } from "./globals";
import { FooterComponent } from "./footer.component";
import { NavigationComponent } from "./navigation.component";
import { NavigationDetailsFullComponent } from "./navigation-details-full.component";
import { ContactFormComponent } from "./contact-form.component";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ScrollToModule,
    FooterComponent,
    NavigationComponent,
    NavigationDetailsFullComponent,
    ContactFormComponent,
  ],
  selector: "toronto-listing-details-full",
  templateUrl: "./toronto-listing-details-full.component.html",
  styleUrls: ["./listing-details-full.component.css"],
})
export class TorontoListingDetailsFullComponent implements OnInit {
  private trackingURLStr = "/classes/REP_RETSTracking.php"; // URL to web api
  selectedListing: ListingDetail | undefined;
  loadedBln: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private listingsService: TorontoListingsService,
    private SEOService: SEOService,
    public globals: Globals,
    private _sanitizer: DomSanitizer,
  ) {
    // load the page details
    this.loadDetails();
  }

  ngOnInit(): void {}

  loadDetails(): void {
    //console.log('detail indirect');

    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) =>
          this.listingsService.getListing(
            params.get("MLSNum"),
            params.get("sourceStr"),
          ),
        ),
      )
      .subscribe((listingsDetails) => {
        listingsDetails = this.fixImages(listingsDetails as ListingDetail);
        this.selectedListing = listingsDetails as ListingDetail;
        console.log(listingsDetails);
        this.loadedBln = true;
        // track the event
        this.trackEvent({ _eventStr: "view-listing" });
        // set the seo data
        this.setSEOTags();
      });
  }

  private fixImages(listingDetailsCls: ListingDetail) {
    // define properties
    var imageArr = listingDetailsCls.imageArr;
    // iterate through the image array to fix the image URL's to the new serverless image resize solution
    for (var i = 0; i < imageArr.length; i++) {
      // define properties
      var urlStr = imageArr[i];
      // change the url to the new one
      urlStr = urlStr.replace(
        "http://dhauv7lm2gh1r.cloudfront.net/",
        "https://d3mtjy7k2lo3ql.cloudfront.net/fit-in/1900x1200/",
      );
      // update the array item
      listingDetailsCls.imageArr[i] = urlStr;
    }
    // return the listing details class
    return listingDetailsCls;
  }

  private setSEOTags() {
    // define properties
    var listingDetailsCls = this.selectedListing;
    if (listingDetailsCls == undefined) return;
    var addressStr = listingDetailsCls.addressStr;
    var addressHyphenStr = listingDetailsCls.addressHyphenStr;
    var cityStr = listingDetailsCls.cityStr;
    var cityHypenStr = listingDetailsCls.cityHyphenStr;
    var stateStr = listingDetailsCls.stateStr;
    var postalCodeStr = listingDetailsCls.postalCodeStr;
    var MLSNum = listingDetailsCls.MLSNum;
    var titleStr =
      addressStr +
      ", " +
      cityStr +
      ", " +
      stateStr +
      " " +
      postalCodeStr +
      " (MLS #" +
      MLSNum +
      ") :: Cayman Marshall International Realty Inc., Brokerage";
    var descriptionStr =
      "Residential property for sale in " +
      cityStr +
      ", " +
      stateStr +
      " (MLS #" +
      MLSNum +
      "). Learn more from Cayman Marshall.";
    var keywordsStr =
      "Real Estate, Houses, " +
      addressStr +
      ", " +
      cityStr +
      ", " +
      stateStr +
      ", Residential, Full Service, $/Sf/Month, Yes, No";
    // set the SEO data
    this.SEOService.setMetaTags({
      _titleStr: titleStr,
      _descriptionStr: descriptionStr,
      _urlStr:
        "https://m.oakvillehomerealestate.net/search/fulldetails/" +
        addressHyphenStr +
        "/" +
        cityHypenStr +
        "/" +
        MLSNum,
      _keywordsStr: keywordsStr,
    });
  }

  getBackground(image: string) {
    // deine properties
    var urlStr = image.replace(
      "http://dhauv7lm2gh1r.cloudfront.net/",
      "https://d3mtjy7k2lo3ql.cloudfront.net/fit-in/1900x1200/",
    );
    return this._sanitizer.bypassSecurityTrustStyle(`url(${urlStr})`);
  }

  public trackEvent(obj: { _eventStr: string }) {
    let headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded;",
      "X-Requested-With": "XMLHttpRequest",
    });
    const options = {
      headers: headers,
      params: this.generateEventTrackingData(obj),
    };
    // track the even
    this.http.post(this.trackingURLStr, options).subscribe(
      (response) => {
        //console.log(response.json());
      },
      (error) => {
        this.handleError(error); // Centralized error handling
      },
    );
  }

  private generateEventTrackingData(obj: { _eventStr: any }) {
    // define properties
    var emailStr = this.globals.emailStr;
    var userIDStr = this.globals.userIDStr;
    var ipAddrStr = this.globals.ipAddrStr;
    var sidStr = this.globals.sidStr;
    var idStr = this.globals.idStr;
    var eventStr = obj._eventStr;
    var eventFullStr = this.generateEventFullString();
    // define properties
    var JSONObj = {
      idStr: idStr,
      emailStr: "",
      userIDStr: "",
      ipAddrStr: ipAddrStr,
      sessionIDStr: "",
      eventStr: eventStr,
      eventFullStr: eventFullStr,
      viewStr: "list-view",
    };
    if (emailStr != "") JSONObj.emailStr = emailStr;
    else delete (JSONObj as any)["emailStr"];
    if (userIDStr != "") JSONObj.userIDStr = userIDStr;
    else delete (JSONObj as any)["userIDStr"];
    if (sidStr != "") JSONObj.sessionIDStr = sidStr;
    else delete (JSONObj as any)["sessionIDStr"];
    return JSONObj;
  }

  private generateEventFullString() {
    // define properties
    if (this.selectedListing == undefined) return;
    var MLSNum = this.selectedListing.MLSNum;
    var propertyTypeStr = this.globals.propertyTypeStr;
    var eventFullObj = {
      MLSNum: MLSNum,
      propertyTypeStr: propertyTypeStr,
    };
    // return the json object as a string
    return JSON.stringify(eventFullObj);
  }

  private handleError(error: any): Promise<any> {
    console.error("An error occurred", error);
    return Promise.reject(error.message || error);
  }
}
